/**
 * Profile — standalone profile editor.
 * Shows all approved claims with editing capabilities.
 * No session context — this is the full profile view.
 */

import { useEffect, useState } from 'preact/hooks';
import { useAuth } from '@clerk/clerk-react';
import { route } from 'preact-router';
import { api, ApiError } from '../api/client';
import { ProfileNotes, type CompiledProfileView } from '../components/ProfileNotes';

export function Profile() {
	const { getToken } = useAuth();
	const [compiledView, setCompiledView] = useState<CompiledProfileView | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		loadProfileView();
	}, []);

	async function loadProfileView() {
		try {
			setLoading(true);
			const token = await getToken();
			if (!token) return;
			const view = await api.getProfileView(token);
			setCompiledView(view);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load record');
		} finally {
			setLoading(false);
		}
	}

	async function handleReorganize(target: string, targetSection: string) {
		const token = await getToken();
		if (!token) return;
		try {
			const result = await api.startInterview(token, { type: 'canonical' });
			// Start a refine-focused conversation — corrections happen naturally in conversation
			await api.setSessionFocus(token, result.sessionId, { type: 'refine', target, targetSection });
			route(`/interview/${result.sessionId}`);
		} catch (err) {
			if (err instanceof ApiError && err.upgradeRequired) {
				setError('Free tier includes one session. Upgrade to Pro on your dashboard for unlimited sessions.');
			} else {
				setError(err instanceof Error ? err.message : 'Failed to start session');
			}
		}
	}

	async function handleClaimAction(claimId: string, action: 'approve' | 'reject', editedValue?: unknown) {
		const token = await getToken();
		if (!token) return;

		try {
			await api.claimAction(token, claimId, { action, editedValue });
			// Re-fetch compiled view to reflect the change with correct grouping
			const view = await api.getProfileView(token);
			setCompiledView(view);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to update claim');
		}
	}

	if (loading) {
		return (
			<div class="profile-page">
				<div class="profile-page-container">
					<p class="t-body text-muted">Loading record...</p>
				</div>
				<style>{profilePageStyles}</style>
			</div>
		);
	}

	return (
		<div class="profile-page">
			<div class="profile-page-header">
				<div class="profile-page-nav">
					<button class="btn btn-ghost btn-sm" onClick={() => route('/dashboard')}>
						&larr; Dashboard
					</button>
				</div>
				<div class="profile-page-title-row">
					<div>
						<h1 class="profile-page-title">Edit Record</h1>
						<p class="t-caption text-muted">{compiledView?.stats.totalClaims || 0} claims</p>
					</div>
				</div>
			</div>

			{error && (
				<div class="error-banner" style="margin: 0 1.5rem;">
					<span>{error}</span>
					<button class="error-dismiss" onClick={() => setError(null)}>&times;</button>
				</div>
			)}

			<div class="profile-page-container">
				{!compiledView || compiledView.stats.totalClaims === 0 ? (
					<div class="profile-page-empty">
						<p class="t-body">No claims yet.</p>
						<p class="t-caption text-muted">Start a conversation to build your record.</p>
						<button class="btn btn-action" onClick={() => route('/dashboard')}>
							Go to Dashboard
						</button>
					</div>
				) : (
					<ProfileNotes
						compiledView={compiledView}
						onClaimAction={handleClaimAction}
						editable
						onReorganize={handleReorganize}
					/>
				)}
			</div>

			<style>{profilePageStyles}</style>
		</div>
	);
}

const profilePageStyles = `
	.profile-page {
		min-height: 100vh;
		background: var(--color-bg);
		display: flex;
		flex-direction: column;
	}

	.profile-page-header {
		border-bottom: 1px solid var(--color-border);
		padding: 0.75rem 1.5rem;
		position: sticky;
		top: 0;
		background: var(--color-bg);
		z-index: 100;
	}

	.profile-page-nav {
		margin-bottom: 0.5rem;
	}

	.profile-page-title-row {
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
	}

	.profile-page-title {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		margin: 0;
	}

	.profile-page-container {
		flex: 1;
		max-width: 640px;
		width: 100%;
		margin: 0 auto;
		padding: 0;
	}

	.profile-page-empty {
		text-align: center;
		padding: 3rem 1rem;
	}

	.profile-page-empty .btn {
		margin-top: 1rem;
	}
`;
